import {NoteDetailComments} from './note-detail-comments';
import {NoteDetailContent} from './note-detail-content';
import ConfirmDeleteModal from '@/components/confirm-delete';
import {
  TouchableOpacity,
  View,
  Text,
  TextInput
} from '@/components/react-native';
import SidePanel from '@/components/side-panel';
import {
  createCommentForNote,
  updateCommentForNote,
  deleteCommentForNote
} from '@/firebase/airport-note';
import {useAppSelector} from '@/hooks/redux';
import useAirportNote from '@/stores/airport-note';
import {INoteInfo, ECommentStatus} from '@/types/airport';
import classNames from 'classnames';
import {useEffect, useState} from 'react';

export const AddEditCommentPanel = ({noteItem}: {noteItem: INoteInfo}) => {
  const commentPanelVisible = useAirportNote(
    (state) => state.commentPanelVisible
  );
  const setCommentPanelVisible = useAirportNote(
    (state) => state.setCommentPanelVisible
  );
  const airportNoteInfo = useAirportNote((state) => state.airportNoteInfo);
  const setRefreshComment = useAirportNote((state) => state.setRefreshComment);
  const [commentText, setCommentText] = useState('');
  const [showDelete, setShowDelete] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const user = useAppSelector((state) => state.user.user);
  const isEditing =
    commentPanelVisible?.id && commentPanelVisible?.id !== 'new';

  useEffect(() => {
    if (commentPanelVisible?.content) {
      setCommentText(commentPanelVisible.content);
    } else {
      setCommentText('');
    }
  }, [commentPanelVisible]);

  const handleSubmit = async () => {
    if (!commentText.trim() || !airportNoteInfo?.icao_code || !user) return;

    setIsSubmitting(true);
    try {
      if (isEditing) {
        // Update existing comment
        await updateCommentForNote(noteItem.id, commentPanelVisible?.id, {
          content: commentText.trim()
        });
        console.log('Comment updated successfully');
      } else {
        // Create new comment
        const commentData = {
          note_id: noteItem.id,
          created_by: user.displayName || user.email || 'Unknown User',
          user_id: user.userId,
          content: commentText.trim(),
          created_at: new Date().toISOString(),
          status: ECommentStatus.PENDING
        };

        await createCommentForNote(
          airportNoteInfo.icao_code,
          noteItem.id,
          commentData
        );
        console.log('Comment created successfully');
      }

      setRefreshComment();
      setCommentPanelVisible(null);
      setTimeout(() => {
        (globalThis as any).airportNoteCommentScrollRef?.scrollToEnd({
          animated: true
        });
      }, 300);
    } catch (error) {
      console.error('Error submitting comment:', error);
      // TODO: Show error toast
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!commentPanelVisible?.id || !airportNoteInfo?.icao_code) return;

    try {
      await deleteCommentForNote(
        airportNoteInfo.icao_code,
        noteItem.id,
        commentPanelVisible.id
      );
      console.log('Comment deleted successfully');
      setRefreshComment();
      setShowDelete(false);
      setCommentPanelVisible(null);
    } catch (error) {
      console.error('Error deleting comment:', error);
      // TODO: Show error toast
    }
  };

  const canDelete = isEditing && user?.userId === commentPanelVisible?.user_id;

  return (
    <SidePanel
      onClose={() => setCommentPanelVisible(null)}
      visible={!!commentPanelVisible}
    >
      <View className="bg-[#090024] flex-1 flex-col">
        <View className="flex-row items-center justify-between py-[16px] w-full">
          <TouchableOpacity
            onPress={() => setCommentPanelVisible(null)}
            className="px-[16px]"
          >
            <Text
              fontFamily="Geist"
              className="text-[#38BDF8] text-[16px] font-[400] tracking-[-0.32px]"
            >
              Cancel
            </Text>
          </TouchableOpacity>

          <View className="flex-row items-center justify-center">
            <Text
              fontFamily="Geist"
              className="text-[#E7E5E4] text-[16px] font-[600] tracking-[-0.32px] capitalize"
            >
              {isEditing ? 'Edit Comment' : 'Add Comment'}
            </Text>
          </View>

          <TouchableOpacity
            onPress={handleSubmit}
            disabled={!commentText.trim() || isSubmitting}
            className="px-[16px]"
          >
            <Text
              fontFamily="Geist"
              className={classNames(
                'text-[16px] font-[500] tracking-[-0.32px]',
                {
                  'text-[#38BDF8]': commentText.trim() && !isSubmitting,
                  'text-[#44403C]': !commentText.trim() || isSubmitting
                }
              )}
            >
              {isSubmitting ? 'Saving...' : isEditing ? 'Done  ' : 'Add    '}
            </Text>
          </TouchableOpacity>
        </View>
        <NoteDetailContent item={noteItem} />
        <NoteDetailComments
          noteId={noteItem.id}
          noteItem={noteItem}
          preview={true}
        />

        <View className="w-full" style={{height: 392 + (canDelete ? 72 : 0)}}>
          <View className="px-[16px] py-[18px]">
            <Text
              fontFamily="GeistMono"
              className="text-[#FAFAF9] text-[16px] font-[500] tracking-[-0.32px] leading-[16px] uppercase"
            >
              COMMENT
            </Text>
          </View>

          {/* Comment Input */}
          <View className="px-[16px]">
            <View className="bg-[#010009] rounded-[8px]" style={{height: 300}}>
              <View className="flex-col">
                <TextInput
                  value={commentText}
                  onChangeText={setCommentText}
                  placeholder="Write comment"
                  placeholderTextColor="#A8A29E"
                  multiline
                  className="px-[16px] py-[12px] rounded-lg text-[#FAFAF9] text-[16px] font-[500] tracking-[-0.32px] leading-[24px]"
                  style={{
                    fontFamily: 'Geist',
                    textAlignVertical: 'top'
                  }}
                />
              </View>
            </View>
          </View>

          {canDelete && (
            <View className="flex-col items-center justify-center mt-[24px] py-[16px] px-[16px]">
              <TouchableOpacity
                onPress={() => setShowDelete(true)}
                className="bg-[#7F1D1D4D] p-[12px] rounded-lg w-full"
              >
                <Text
                  fontFamily="Geist"
                  className="text-[#DC2626] text-[16px] font-[500] tracking-[-0.32px] leading-[20px] text-center"
                >
                  Delete Comment
                </Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
        {showDelete && (
          <ConfirmDeleteModal
            title="Confirm Delete"
            description="Please note that this action cannot be undone."
            onClose={() => setShowDelete(false)}
            onConfirm={handleDelete}
          />
        )}
      </View>
    </SidePanel>
  );
};
